import {Button, Space, Spin} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {useState} from 'react';

import {ModalOpenerComponent} from 'components/modal-opener-component';

import {IRisksRequestDetailsDto, IRisksRequestDto} from '../../api/types';
import {RisksRequestsService} from '../../services';
import {RequestDetailsForm} from './request-details-form';
import styles from './request-details-modal.module.less';

interface IRequestDetailsModalProps {
    value: number;
    fields: IRisksRequestDto;
    isCreateAllowed: boolean;
    onRefreshed: () => void;
}

export const RequestDetailsModal = ({value, fields, isCreateAllowed, onRefreshed}: IRequestDetailsModalProps) => {
    const [form] = useForm();
    const [details, setDetails] = useState<IRisksRequestDetailsDto | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const {data} = await RisksRequestsService.getById(value);
            setDetails(data);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const onShow = () => {
        setIsEditMode(false);
        loadData();
    };

    const handleEnterEdit = () => setIsEditMode(true);

    const handleCancelEdit = async () => {
        setIsEditMode(false);
        await loadData();
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const risks = form.getFieldValue('risks') || [];
            const docs: {id: number; activeFlag: boolean}[] = [];
            risks.forEach((r: {documents?: {id: number; activeFlag: boolean}[]}) => {
                (r.documents || []).forEach((d) => {
                    docs.push({id: d.id, activeFlag: !!d.activeFlag});
                });
            });
            await RisksRequestsService.updateActive({docs});
            setIsEditMode(false);
            await loadData();
        } catch (e) {
            console.error(e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setIsRefreshing(true);
            await RisksRequestsService.refresh(value);
            onRefreshed();
            await loadData();
        } catch (e) {
            console.error(e);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <ModalOpenerComponent
            component={<Button type="link">{fields.requestId}</Button>}
            modalProps={{title: 'Сведения о запросе', width: 900}}
            hideControls={{save: true}}
            controlLabels={{cancel: 'Закрыть'}}
            onShow={onShow}
            shouldConfirm={() => false}
            handleCancel={(setIsModalOpen) => setIsModalOpen(false)}
        >
            <Spin
                spinning={isLoading}
                tip="Загрузка информации о запросе..."
            >
                {isCreateAllowed && details && (
                    <Space className={styles['request-details-modal__actions']}>
                        {isEditMode ? (
                            <>
                                <Button
                                    type="default"
                                    onClick={handleCancelEdit}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    type="primary"
                                    loading={isSaving}
                                    onClick={handleSave}
                                >
                                    Сохранить
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="primary"
                                    loading={isRefreshing}
                                    onClick={handleRefresh}
                                >
                                    Обновить данные
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleEnterEdit}
                                >
                                    Редактировать активность
                                </Button>
                            </>
                        )}
                    </Space>
                )}
                {details && (
                    <RequestDetailsForm
                        formInstance={form}
                        details={details}
                        isEditMode={isEditMode}
                    />
                )}
            </Spin>
        </ModalOpenerComponent>
    );
};
